import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Linking } from 'react-native';
import i18n from '@/src/lib/i18n';
import sentry from '@/src/utils/sentry';

const SUPPORT_EMAIL = 'suporte@alldrivers.pt';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /**
   * Optional label identifying which shell this boundary wraps (e.g. 'root',
   * 'dashboard'). Sent to Sentry as a tag so we can tell where a render crash
   * happened.
   */
  boundary?: string;
  /** Optional override for the fallback UI (mainly for tests/storybook). */
  fallback?: (reset: () => void) => React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Global React error boundary. Catches render-time crashes anywhere below it,
 * reports them to Sentry with component-stack + boundary context, and shows a
 * recoverable fallback (retry + contact support) instead of a white screen.
 *
 * Deliberately uses plain React Native primitives (no Paper / theme provider)
 * so the fallback still renders even when the crash originated inside a
 * provider higher in the tree.
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    sentry.captureException(error, (scope) => {
      scope.setTag('error_boundary', this.props.boundary ?? 'root');
      scope.setExtra('componentStack', errorInfo.componentStack);
      return scope;
    });
  }

  private handleRetry = () => {
    this.setState({ hasError: false });
  };

  private handleContactSupport = () => {
    const subject = encodeURIComponent('AllDrivers - App error report');
    Linking.openURL(`mailto:${SUPPORT_EMAIL}?subject=${subject}`).catch(() => {
      // Mail client unavailable (e.g. web without handler) — nothing else to do.
    });
  };

  private translate(key: string, fallback: string): string {
    try {
      return i18n.t(key, fallback);
    } catch {
      return fallback;
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback(this.handleRetry);
    }

    return (
      <View style={styles.container} testID="error-boundary-fallback">
        <Text style={styles.title}>
          {this.translate('errorBoundary.title', 'Algo correu mal')}
        </Text>
        <Text style={styles.message}>
          {this.translate(
            'errorBoundary.message',
            'Ocorreu um erro inesperado. Já fomos notificados. Podes tentar novamente ou contactar o suporte.',
          )}
        </Text>
        <TouchableOpacity
          style={styles.primaryButton}
          onPress={this.handleRetry}
          accessibilityRole="button"
          testID="error-boundary-retry"
        >
          <Text style={styles.primaryButtonText}>
            {this.translate('errorBoundary.retry', 'Tentar novamente')}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.secondaryButton}
          onPress={this.handleContactSupport}
          accessibilityRole="button"
          testID="error-boundary-support"
        >
          <Text style={styles.secondaryButtonText}>
            {this.translate('errorBoundary.contactSupport', 'Contactar suporte')}
          </Text>
        </TouchableOpacity>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    backgroundColor: '#FEFBFF',
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#1B1B1F',
    textAlign: 'center',
    marginBottom: 12,
  },
  message: {
    fontSize: 15,
    lineHeight: 22,
    color: '#45464F',
    textAlign: 'center',
    marginBottom: 28,
  },
  primaryButton: {
    backgroundColor: '#017CC7',
    paddingVertical: 12,
    paddingHorizontal: 28,
    borderRadius: 8,
    marginBottom: 12,
    minWidth: 220,
    alignItems: 'center',
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButton: {
    paddingVertical: 12,
    paddingHorizontal: 28,
    borderRadius: 8,
    minWidth: 220,
    alignItems: 'center',
  },
  secondaryButtonText: {
    color: '#017CC7',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default ErrorBoundary;
